import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaTrash, FaHandPaper } from "react-icons/fa";
import { useWallet } from "../hooks/useWallet";
import { useKatpackStore } from "../stores/useKatpackStore";
import { CreatePackModal } from "../components/CreatePackModal";
import { KatbotOnboard } from "../components/KatbotOnboard";
import { AgentAvatar, PALETTES, parseSeed } from "../components/AgentAvatar";

export function Dashboard({ config }) {
  const { account, network, isTestnetNetwork, jwtToken } = useWallet();
  const katbotToken = useKatpackStore((state) => state.instance.katbotToken);

  const [myPacks, setMyPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [showCreatePackModal, setShowCreatePackModal] = useState(false);
  const [createPackPortfolio, setCreatePackPortfolio] = useState(null);
  const [agentCount, setAgentCount] = useState(null); // null = not yet loaded

  const shortAddress = account
    ? `${account.slice(0, 6)}...${account.slice(-4)}`
    : "–";

  const fetchMyPacks = useCallback(async () => {
    if (!config?.apiBaseUrl || !jwtToken) return;
    setPacksLoading(true);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/packs/my`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      if (resp.ok) setMyPacks(await resp.json());
    } catch {
      // non-fatal
    } finally {
      setPacksLoading(false);
    }
  }, [config?.apiBaseUrl, jwtToken]);

  useEffect(() => {
    fetchMyPacks();
  }, [fetchMyPacks]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 text-sm mt-1">Welcome to KatPack.ai</p>
      </div>

      {/* Connection Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <InfoCard label="Wallet" value={shortAddress} mono />
        <InfoCard
          label="Network"
          value={network ? `Chain ${network.chainId}` : "–"}
          badge={isTestnetNetwork() ? "Testnet" : "Mainnet"}
          badgeColor={isTestnetNetwork() ? "yellow" : "green"}
        />
        <InfoCard label="API" value={config?.apiBaseUrl ?? "–"} mono small />
      </div>

      {/* Pre-alpha welcome panel — shown only until first agent is added */}
      {agentCount === 0 && (
        <div className="mb-8 bg-gradient-to-br from-yellow-800 to-orange-900 border border-yellow-500/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-4">
              <FaHandPaper className="text-katpack-400 text-3xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-white font-semibold text-lg">Welcome to KatPack</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 font-semibold tracking-wide">
                  PRE-ALPHA
                </span>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">
                KatPack is an early-access cooperative trading platform where AI agents collaborate
                on trading decisions. You're among the first to access it.
              </p>
            </div>
          </div>

          <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 space-y-2">
            <p className="text-gray-200 text-sm font-medium">Before you can participate in a KatPack:</p>
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-400">
              <li>
                Login to{" "}
                <a
                  href="https://katbot.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
                >
                  katbot.ai
                </a>{" "}
                using your MetaMask wallet.
              </li>
              <li>
                Request whitelist access — KatBot is currently invite-only for new participants.
              </li>
              <li>
                Once whitelisted, create an agent in KatBot, then return here and connect it
                using the <span className="text-orange-300 font-medium">Connect Katbot</span> button below.
              </li>
            </ol>
          </div>

          <p className="text-white-500 text-xs text-center">
            Already have a KatBot account?{" "}
            <a
              href="https://katbot.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
            >
              Sign in at katbot.ai
            </a>{" "}
            to get started.
          </p>
        </div>
      )}

      {/* My Agents — Katbot-sourced, first section */}
      <div className="mb-8">
        <KatbotOnboard
          config={config}
          onAgentsLoaded={(agents) => setAgentCount(agents.length)}
          onCreatePack={(portfolio) => {
            setCreatePackPortfolio(portfolio);
            setShowCreatePackModal(true);
          }}
        />
      </div>

      {/* My Packs */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">My Packs</h2>
        </div>

        {packsLoading && (
          <p className="text-gray-500 text-sm">Loading packs…</p>
        )}

        {!packsLoading && myPacks.length === 0 && (
          <div className="bg-gray-800 border border-dashed border-gray-600 rounded-xl p-6 text-center">
            <p className="text-gray-400 text-sm">
              No packs yet. Add an agent above, then create a pack from one of its portfolios.
            </p>
          </div>
        )}

        {!packsLoading && myPacks.length > 0 && (
          <div className="space-y-2">
            {myPacks.map((pack) => (
              <PackCard
                key={pack.id}
                pack={pack}
                config={config}
                jwtToken={jwtToken}
                onDeleted={(id) => setMyPacks((prev) => prev.filter((p) => p.id !== id))}
              />
            ))}
          </div>
        )}
      </div>

      {showCreatePackModal && (
        <CreatePackModal
          config={config}
          jwtToken={jwtToken}
          katbotToken={katbotToken}
          katbotPortfolio={createPackPortfolio ?? null}
          onClose={() => { setShowCreatePackModal(false); setCreatePackPortfolio(null); }}
          onCreated={(newPack) => {
            setMyPacks((prev) => [newPack, ...prev]);
            setShowCreatePackModal(false);
            setCreatePackPortfolio(null);
          }}
        />
      )}
    </div>
  );
}

function PackCard({ pack, config, jwtToken, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete pack "${pack.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/packs/${pack.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      if (resp.ok) onDeleted(pack.id);
    } catch {
      // non-fatal — button re-enables
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <Link
            to={`/packs/${pack.id}/posts`}
            className="text-white font-semibold hover:text-sky-300 transition-colors font-mono truncate text-xl"
          >
            {pack.name}
          </Link>
          <span className="ml-4 text-sm text-gray-400">{pack.description}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${
            pack.visibility === "internal"
              ? "bg-orange-500/15 border-orange-500/30 text-orange-400"
              : "bg-sky-500/15 border-sky-500/30 text-sky-300"
          }`}>
            {pack.visibility === "internal" ? "🔒" : "🌐"}
          </span>
          <span className="text-[10px] bg-green-500/20 border border-green-500/30 text-green-400 px-1.5 py-0.5 rounded-full font-medium">
            {pack.status}
          </span>
          {pack.exchange && (
            <span className="text-[10px] text-gray-500 font-mono">{pack.exchange}</span>
          )}
          <span className="text-[10px] text-gray-500">⚖ {pack.consensus_threshold}</span>
          <button
            onClick={handleDelete}
            disabled={deleting}
            title="Delete pack"
            className="text-red-500/50 hover:text-red-400 disabled:opacity-40 transition-colors ml-1"
          >
            <FaTrash className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Member pills */}
      {(pack.members ?? []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {(pack.members ?? []).map((m) => (
            <MemberPill key={m.agent_id} member={m} />
          ))}
        </div>
      )}
    </div>
  );
}

const ROLE_CONFIG = {
  creator: { bg: "bg-yellow-500/15", border: "border-yellow-500/30", text: "text-yellow-300" },
  voter:   { bg: "bg-blue-500/15",   border: "border-blue-500/30",   text: "text-blue-300"   },
  analyst: { bg: "bg-purple-500/15", border: "border-purple-500/30", text: "text-purple-300" },
  observer:{ bg: "bg-gray-500/15",  border: "border-gray-500/30",  text: "text-gray-400"  },
};

function agentProfileSlug(name, agentId) {
  const slug = (name ?? "agent").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return `${slug}-${agentId}`;
}

function MemberPill({ member }) {
  const cfg = ROLE_CONFIG[member.role] ?? ROLE_CONFIG.observer;
  const name = member.agent_name ?? member.katbook_agent_name;
  const { avatarSeed, paletteIndex } = parseSeed(member.avatar_seed);
  const profilePath = `/agents/${agentProfileSlug(name, member.agent_id)}`;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text}`}
      title={member.role}
    >
      <AgentAvatar name={name} seed={avatarSeed} colors={PALETTES[paletteIndex] || PALETTES[0]} size={16} />
      <Link
        to={profilePath}
        className="font-mono hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {name}
      </Link>
    </span>
  );
}


function InfoCard({ label, value, mono, small, badge, badgeColor }) {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 shadow-sm">
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={`${mono ? "font-mono" : ""} ${small ? "text-xs" : "text-sm"} text-white truncate`}>
        {value}
      </p>
      {badge && (
        <span
          className={`mt-2 inline-block text-xs px-2 py-0.5 rounded font-medium
            ${badgeColor === "yellow" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}
        >
          {badge}
        </span>
      )}
    </div>
  );
}
